import { instance } from "@/util/Alerts";

import { ActionContext } from "vuex";
type PolymerContext = ActionContext<PolymerState, RootState>;

const state = (): PolymerState => ({
    statusSelect: "active",
    statusOptions: [
        { value: "active", label: "Active", disabled: false },
        { value: "inactive", label: "Inactive", disabled: false },
        { value: "de-active", label: "De-active", disabled: false },
        { value: "test", label: "Test", disabled: false }
    ] as BaseDropdownOption[],
    cathodeType: "Poly",
    seriesSelect: "",
    seriesOptions: [] as BaseDropdownOption[],
    sizeSelect: "",
    sizeOptions: [] as BaseDropdownOption[],
    terminationSelect: "",
    terminationDefaults: [
        { value: "T", label: "100% Matte Sn - T", disabled: false },
        { value: "H", label: "Standard Solder (SnPb) - H", disabled: false },
        { value: "N", label: "Non-Magnetic (Sn) - N", disabled: false },
        { value: "M", label: "Non-Magnetic (SnPb) - M", disabled: false },
        { value: "A", label: "Nickel Gold - A", disabled: false },
        { value: "G", label: "Gold - G", disabled: false },
        { value: "K", label: "Solder Fused - K", disabled: false },
        { value: "C", label: "Hot Solder Dipped - C", disabled: false },
        { value: "P", label: "Ni-Pd-Au - P", disabled: false }
    ],
    terminationOptions: [] as BaseDropdownOption[],
    voltageSelect: "",
    voltageOptions: [] as BaseDropdownOption[],
    capacitorList: [] as CapacitorModel[],
});

const getters = {
};

const mutations = {
    setStatusSelect(state: PolymerState, payload: string) {
        state.statusSelect = payload;
    },
    setSeriesSelect(state: PolymerState, payload: string) {
        state.seriesSelect = payload;
    },
    setSizeSelect(state: PolymerState, payload: string) {
        state.sizeSelect = payload;
    },
    setTerminationSelect(state: PolymerState, payload: string) {
        state.terminationSelect = payload;
    },
    setVoltageSelect(state: PolymerState, payload: string) {
        state.voltageSelect = payload;
    },
    setSeriesOptions(state: PolymerState, payload: BaseDropdownOption[]) {
        state.seriesOptions = payload;
    },
    setSizeOptions(state: PolymerState, payload: BaseDropdownOption[]) {
        state.sizeOptions = payload;
    },
    setTerminationOptions(state: PolymerState, payload: BaseDropdownOption[]) {
        state.terminationOptions = payload;
    },
    setVoltageOptions(state: PolymerState, payload: BaseDropdownOption[]) {
        state.voltageOptions = payload;
    },
    setCapacitors(state: PolymerState, payload: CapacitorModel[]) {
        state.capacitorList = payload;
    },
};

const actions = {
    setStatusSelect(context: PolymerContext, payload: string) {
        context.commit("setStatusSelect", payload);
    },
    setSeriesSelect(context: PolymerContext, payload: string) {
        context.commit("setSeriesSelect", payload);
    },
    setSizeSelect(context: PolymerContext, payload: string) {
        context.commit("setSizeSelect", payload);
    },
    setTerminationSelect(context: PolymerContext, payload: string) {
        context.commit("setTerminationSelect", payload);
    },
    setVoltageSelect(context: PolymerContext, payload: string) {
        context.commit("setVoltageSelect", payload);
    },
    getSeriesOptions(context: PolymerContext) {
        const url = "series";
        const body = {
            type: "tantalum",
            cathode: context.state.cathodeType,
            partStatus: context.state.statusSelect
        };
        instance.post(url, body).then((response) => {
            const options = [] as BaseDropdownOption[];
            if (!response.data || !Array.isArray(response.data) || response.data.length === 0) {
                context.commit("setSeriesOptions", options);
                return;
            }
            for (let i = 0; i < response.data.length; i++) {
                const value = response.data[i]["series"];
                const label = response.data[i]["description"];
                options.push({
                    value,
                    label: value + ": " + label,
                    disabled: false
                } as BaseDropdownOption);
            }
            context.commit("setSeriesOptions", options);
        }).catch(() => {
            context.commit("setSeriesOptions", [] as BaseDropdownOption[]);
        });
    },
    getSizeOptions(context: PolymerContext) {
        if (
            context.state.statusSelect === "active" &&
            (context.state.seriesSelect === "")
        ) {
            context.commit("setSizeOptions", [] as BaseDropdownOption[]);
            return;
        }
        const url = "size";
        const body = {
            type: "tantalum",

            series: context.state.seriesSelect,
            partStatus: context.state.statusSelect
        };
        instance.post(url, body).then((response) => {
            const options = [] as BaseDropdownOption[];
            if (!response.data || !Array.isArray(response.data) || response.data.length === 0) {
                context.commit("setSizeOptions", options);
                return;
            }
            for (let i = 0; i < response.data.length; i++) {
                const value = response.data[i]["case_Size"];
                const label = response.data[i]["eiA_Code"];
                options.push({
                    value,
                    label: "(" + value + ") " + label,
                    disabled: false
                });
            }
            context.commit("setSizeOptions", options);
        }).catch(() => {
            context.commit("setSizeOptions", [] as BaseDropdownOption[]);
        });
    },
    getTerminationOptions(context: PolymerContext) {
        if (
            context.state.statusSelect === "active" &&
            (context.state.seriesSelect === "")
        ) {
            context.commit("setTerminationOptions", [] as BaseDropdownOption[]);
            return;
        }
        const url = "termination";
        const body = {
            type: "tantalum",

            series: context.state.seriesSelect,
            partStatus: context.state.statusSelect
        };
        instance.post(url, body).then((response) => {
            const options = [] as BaseDropdownOption[];
            if (!response.data || !Array.isArray(response.data) || response.data.length === 0) {
                context.commit("setTerminationOptions", options);
                return;
            }
            const terminations = response.data[0].split(""); //returns a string, e.g. "THCG"
            for (let i = 0; i < context.state.terminationDefaults.length; i++) {
                if (terminations.includes(context.state.terminationDefaults[i].value)) {
                    options.push(context.state.terminationDefaults[i]);
                }
            }
            context.commit("setTerminationOptions", options);
        }).catch(() => {
            context.commit("setTerminationOptions", [] as BaseDropdownOption[]);
        });
    },
    getVoltageOptions(context: PolymerContext) {
        if (
            context.state.statusSelect === "active" &&
            (context.state.seriesSelect === "" ||
                context.state.sizeSelect === "")
        ) {
            context.commit("setVoltageOptions", [] as BaseDropdownOption[]);
            return;
        }
        /* We can at least check for any of the body properties == "" and prevent
           an API call which we already know would return an empty response.
           We will need to check for empty responses, because empty responses
           can result from invalid selections (e.g. an invalid size selection
           due to the dropdowns still updating.
           Ideally, we create a flag for the component when all loading has finished.
           Need to implement some kind of loading indication anyways.
           */
        const url = "voltage";
        const body = {
            type: "tantalum",

            series: context.state.seriesSelect,
            size: context.state.sizeSelect,
            partStatus: context.state.statusSelect
        };
        instance.post(url, body).then((response) => {
            const options = [] as BaseDropdownOption[];
            if (!response.data || !Array.isArray(response.data) || response.data.length === 0) {
                context.commit("setVoltageOptions", options);
                return;
            }
            for (let i = 0; i < response.data.length; i++) {
                // To match SQL query we need 6 decimal points.
                const value = response.data[i].toFixed(6).toString();
                const label = response.data[i].toString();
                options.push({
                    value,
                    label,
                    disabled: false
                });
            }
            context.commit("setVoltageOptions", options);
        }).catch(() => {
            context.commit("setVoltageOptions", [] as BaseDropdownOption[]);
        });
    },
    getCapacitors(context: PolymerContext) {
        if (
            context.state.statusSelect === "active" &&
            (context.state.seriesSelect === "" ||
                context.state.sizeSelect === "" ||
                context.state.terminationSelect === "" ||
                context.state.voltageSelect === "")
        ) {
            context.commit("setCapacitors", [] as CapacitorModel[]);
            return;
        }
        /* We can at least check for any of the body properties == "" and prevent
           an API call which we already know would return an empty response.
           We will need to check for empty responses, because empty responses
           can result from invalid selections (e.g. an invalid size selection
           due to the dropdowns still updating.
           Ideally, we create a flag for the component when all loading has finished.
           Need to implement some kind of loading indication anyways.
           */
        const url = "capacitance";
        const body = {
            type: "tantalum",

            series: context.state.seriesSelect,
            size: context.state.sizeSelect,
            termination: context.state.terminationSelect,
            voltage: context.state.voltageSelect,
            partStatus: context.state.statusSelect
        };
        instance.post(url, body).then((response) => {
            const capacitorList = [] as CapacitorModel[];
            if (!response.data || !Array.isArray(response.data) || response.data.length === 0) {
                context.commit("setCapacitors", capacitorList);
                return;
            }
            for (let i = 0; i < response.data.length; i++) {
                capacitorList.push({
                    id: response.data[i].id,
                    instances: 1,
                    capDisplay: response.data[i].capDisplay,
                    dielectric: response.data[i].dielectric,
                    maxTemp: response.data[i].maxTemp,
                    kemetPn: response.data[i].kemetPn,
                    basePn: response.data[i].basePn,
                    series: response.data[i].series,
                    appDef: response.data[i].appDef,
                    spicePn: response.data[i].spicePn,
                    capType: response.data[i].capType,
                    capValue: response.data[i].capValue,
                    voltageRating: response.data[i].voltageRating,
                    ceProps: [] as UpdatedCapCeProps,
                    hidden: false,
                    param: response.data[i].param,
                    tolerance: {
                        title: "Tolerance",
                        select: "K",
                        disabled: false,
                        options: [
                            { value: "K", label: "10%", disabled: false } as BaseDropdownOption,
                            { value: "M", label: "20%", disabled: false } as BaseDropdownOption
                        ] as Array<BaseDropdownOption>,
                    } as InputDropdown,
                    filmProps: {} as FilmProps,
                    seedProps: [] as SeedProp[],
                    displayAllCeProps: false,
                    tcc: response.data[i].tcc,
                    vcac: response.data[i].vcac,
                });
            }
            context.commit("setCapacitors", capacitorList);
        }).catch(() => {
            context.commit("setCapacitors", [] as CapacitorModel[]);
        });
    }
};

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions
}